import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import type { z } from 'zod';
import { newsletterSubscribeSchema } from '@portfolio/shared';
import { api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useLocale } from './i18n';

type NewsletterValues = z.input<typeof newsletterSubscribeSchema>;

/** How long the confirmation message stays before the email field comes back. */
const MESSAGE_MS = 5000;

/**
 * Newsletter signup row at the top of the dark footer: heading and intro at the start, email +
 * consent at the end (errors use a lighter red so they stay legible on the dark band). The API sends a confirmation email (double opt-in) before subscribing.
 */
export function NewsletterSignup({ heading, intro }: { heading: string; intro: string | null }) {
  const { t, locale } = useLocale();
  const [message, setMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewsletterValues>({
    resolver: zodResolver(newsletterSubscribeSchema),
    defaultValues: { website: '' },
  });
  const mutation = useMutation({
    mutationFn: (v: NewsletterValues) => api.post<{ message: string }>('/newsletter/subscribe', v),
    onSuccess: () => {
      setMessage(t.newsletter.sent);
      reset();
    },
  });

  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), MESSAGE_MS);
    return () => window.clearTimeout(timer);
  }, [message]);

  // Validation messages come from the shared (English) schema; Arabic visitors see their own.
  const errorText = (msg: string | undefined) =>
    locale === 'en'
      ? msg
      : msg === 'Email is required'
        ? t.newsletter.emailRequired
        : t.newsletter.emailInvalid;

  return (
    <section
      aria-labelledby="newsletter-title"
      // grid-cols-1 (minmax(0,1fr)) on phones: an auto column would grow to the email input's
      // intrinsic width plus the button and push the row off the screen.
      className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,500px)] md:items-end md:gap-x-16"
    >
      <div>
        <h2 id="newsletter-title" className="text-title font-bold">
          {heading}
        </h2>
        {intro && <p className="mt-3 max-w-[460px] text-body text-graphite">{intro}</p>}
      </div>

      {message ? (
        <p role="status" className="flex items-start gap-3 text-body text-white/85">
          <CheckCircle2 className="mt-1 size-5 shrink-0 text-primary" />
          {message}
        </p>
      ) : (
        <form noValidate onSubmit={handleSubmit((v) => mutation.mutate(v))} className="relative">
          <div
            className={cn(
              'flex items-center gap-3 border-b transition-colors focus-within:border-primary',
              errors.email ? 'border-red-300' : 'border-graphite',
            )}
          >
            <label htmlFor="n-email" className="sr-only">
              {t.newsletter.emailLabel}
            </label>
            <input
              id="n-email"
              type="email"
              autoComplete="email"
              placeholder={t.newsletter.placeholder}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'n-email-err' : undefined}
              className="h-14 min-w-0 flex-1 bg-transparent text-lead font-light text-white placeholder:text-graphite focus:outline-none"
              {...register('email')}
            />
            <button
              type="submit"
              disabled={mutation.isPending}
              className="mb-2 inline-flex h-11 shrink-0 items-center gap-2 rounded-lg bg-primary px-5 text-ui font-semibold text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
            >
              {mutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <ArrowRight className="size-4 rtl:-scale-x-100" aria-hidden />
              )}
              {t.newsletter.subscribe}
            </button>
          </div>
          {errors.email && (
            <p id="n-email-err" role="alert" className="mt-2 text-caption text-red-300">
              {errorText(errors.email.message)}
            </p>
          )}
          <label className="mt-4 flex items-start gap-2.5 text-caption text-graphite">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-[#FA9A21]"
              {...register('consent')}
            />
            <span>{t.newsletter.consent}</span>
          </label>
          {errors.consent && (
            <p role="alert" className="mt-2 text-caption text-red-300">
              {t.newsletter.consentRequired}
            </p>
          )}
          {/* honeypot */}
          <div aria-hidden className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <input tabIndex={-1} autoComplete="off" {...register('website')} />
          </div>
          {mutation.isError && (
            <p role="alert" className="mt-2 text-caption text-red-300">
              {t.states.apiError ?? errorMessage(mutation.error)}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
